import { TransferCompletionMethod } from '@repo/types'

/**
 * The ownership EVIDENCE LADDER, as three words.
 *
 * Each rung says strictly more than the one below it:
 *   `transferred`  — the ledger moved. Nobody has proved anything beyond that.
 *   `claimed`      — the recipient authenticated, so a person accepted it.
 *   `scan_claimed` — that person also scanned the physical tag.
 *
 * Short on purpose: this renders in the right-hand value column of a three-row table
 * on a 375px phone, beside "Owner since" and "Acquired via" (working-conventions §25).
 *
 * ## Why this is a function and not a component any more
 *
 * It was `TransferCompletionChip`, a wrapper that borrowed `AuthenticChip`'s pill so
 * `scan_claimed` inherited the "Authentic" tone and the other two the "Unverified" one.
 * Two instances of that pill existed. The certificate HERO one was removed for being an
 * unlabelled badge with no context; this one was deliberately KEPT, because the
 * "Verification" label beside it supplies exactly the context the hero lacked.
 *
 * What stayed wrong was the SHAPE, not the presence. A pill in a column of plain values
 * reads as a control that wandered into a table rather than as that table's third value.
 * So the pill goes and the strings stay — they are the part carrying meaning, and
 * keeping them here rather than inlining them at the one call site is what stops a
 * second surface inventing a fourth word for the same rung (§36).
 *
 * Returns `null` for a missing method — no transfer, or a legacy row predating the
 * column — so the caller renders nothing rather than an empty value cell.
 */
const COMPLETION_METHOD_LABELS: Record<TransferCompletionMethod, string> = {
  [TransferCompletionMethod.Transferred]: 'Transferred',
  [TransferCompletionMethod.Claimed]: 'Claimed',
  [TransferCompletionMethod.ScanClaimed]: 'Scan verified',
}

export const transferCompletionLabel = (
  completionMethod?: TransferCompletionMethod | null
): string | null => {
  if (!completionMethod) return null
  return COMPLETION_METHOD_LABELS[completionMethod] ?? null
}

export default transferCompletionLabel
